// src/whatsappChat/api/cache.js
// Writes to the react-query cache shared by the REST hooks and the socket.
//   ['wa-conversations', { search, filter, numberId }]  infinite query, pages of { data, has_more }
//   ['wa-thread', id]  { messages, hasMoreOlder, windowOpen, conversation, subscriber, accessLost }
import { replaceEqualDeep } from '@tanstack/react-query';
import { notifyWaUnreadChanged, waChatClient } from './client';

/**
 * Add or replace rows by id. A row that came back identical keeps its old object,
 * and when nothing changed at all the `existing` array itself is returned, so a
 * poll with no news re-renders nothing.
 */
export function mergeMessages(existing = [], incoming = []) {
  const byId = new Map(existing.map((m) => [m.id, m]));
  incoming.forEach((m) => {
    if (m?.id == null) return;
    byId.set(m.id, replaceEqualDeep(byId.get(m.id), m));
  });
  const next = Array.from(byId.values()).sort((a, b) => a.id - b.id);
  const same = next.length === existing.length && next.every((m, i) => m === existing[i]);
  return same ? existing : next;
}

/** Copy `status` and `error` from fresh rows onto the messages already shown, matched by id. */
export function applyStatuses(existing = [], incoming = []) {
  const fresh = new Map();
  incoming.forEach((m) => {
    if (m?.id != null) fresh.set(String(m.id), m);
  });
  let changed = false;
  const next = existing.map((m) => {
    const row = fresh.get(String(m.id));
    if (!row || row.status == null) return m;
    const error = row.error ?? null;
    if (row.status === m.status && error === (m.error ?? null)) return m;
    changed = true;
    return { ...m, status: row.status, error };
  });
  return changed ? next : existing;
}

const byNewest =(a, b) => String(b.last_message_at || '').localeCompare(String(a.last_message_at || ''));

function matchesFilter(row, { filter, numberId } = {}) {
  if (numberId && row.number_id !== numberId) return false;
  if (filter === 'unread') return row.unread_count > 0;
  if (filter === 'unknown') return row.subscriber_id == null;
  return true;
}

/** Insert or replace one inbox row in every cached list it belongs to. */
export function upsertConversation(qc, row) {
  qc.getQueriesData({ queryKey: ['wa-conversations'] }).forEach(([key, data]) => {
    if (!data?.pages) return;
    const params = key[1] || {};

    let found = null;
    data.pages.forEach((page) => {
      (page?.data || []).forEach((r) => {
        if (r.id === row.id) found = r;
      });
    });

    if (!found) {
      if (!matchesFilter(row, params)) return;
      // Only the server knows whether a new row matches a search.
      if (params.search) {
        qc.invalidateQueries({ queryKey: key, exact: true });
        return;
      }
    }

    let pages;
    if (found && found.last_message_at === row.last_message_at) {
      pages = data.pages.map((page) => ({
        ...page,
        data: (page?.data || []).map((r) => (r.id === row.id ? row : r)),
      }));
    } else {
      pages = data.pages.map((page) => ({
        ...page,
        data: (page?.data || []).filter((r) => r.id !== row.id),
      }));
      const first = pages[0] || { data: [], has_more: false };
      pages[0] = { ...first, data: [row, ...(first.data || [])].sort(byNewest) };
    }

    qc.setQueryData(key, { ...data, pages });
  });
}

export function setConversationRead(qc, id) {
  qc.setQueriesData({ queryKey: ['wa-conversations'] }, (data) =>
    data?.pages
      ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: (page?.data || []).map((r) => (r.id === id ? { ...r, unread_count: 0 } : r)),
          })),
        }
      : data
  );
}

export async function markConversationRead(qc, id) {
  if (!id) return;
  try {
    await waChatClient.post('/conversations/read', { conversation_id: id });
    setConversationRead(qc, id);
    notifyWaUnreadChanged();
  } catch {
    // Unread count simply stays as it was.
  }
}

export function patchThread(qc, id, updater) {
  const key = ['wa-thread', Number(id)];
  const prev = qc.getQueryData(key);
  if (!prev) return;
  const patch = updater(prev);
  // Nothing new: leave the cache alone so the open chat does not re-render.
  if (Object.keys(patch).every((field) => patch[field] === prev[field])) return;
  qc.setQueryData(key, { ...prev, ...patch });
}
